
import { defineComponent, ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  getAdminStats,
  getCostBenefitAnalytics,
  getFunnelAnalytics,
  getFrequencyAnalytics,
  getCostAnalytics,
  getWillingnessAnalytics,
  getOverviewTrends,
  AdminStats,
  CostBenefitAnalytics,
  FunnelAnalytics,
  FrequencyAnalytics,
  CostAnalytics,
  WillingnessAnalytics,
  OverviewTrendAnalytics,
  DateRangeFilter,
} from '@/services/admin'

type MenuKey = 'users' | 'tasks' | 'points' | 'feedbacks' | 'workers' | 'coupons'
type TrendKey = 'activeUsers' | 'tasks' | 'completedTasks' | 'netChargedPages'

function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function daysBefore(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return formatDate(date)
}

function roundPercent(value: number): string {
  return `${Math.round(value * 100)}%`
}

export default defineComponent({
  name: 'AdminOverview',
  emits: ['navigate'],
  setup(_, { emit }) {
    const presets = [
      { label: '近 7 天', days: 7 },
      { label: '近 30 天', days: 30 },
      { label: '近 90 天', days: 90 },
    ]
    const selectedPreset = ref(30)
    const startDate = ref(daysBefore(29))
    const endDate = ref(formatDate(new Date()))
    const stats = ref<AdminStats | null>(null)
    const statsLoading = ref(false)
    const costBenefit = ref<CostBenefitAnalytics | null>(null)
    const costBenefitLoading = ref(false)
    const funnel = ref<FunnelAnalytics | null>(null)
    const frequency = ref<FrequencyAnalytics | null>(null)
    const costAnalytics = ref<CostAnalytics | null>(null)
    const willingness = ref<WillingnessAnalytics | null>(null)
    const analyticsLoading = ref(false)
    const trends = ref<OverviewTrendAnalytics | null>(null)
    const trendsLoading = ref(false)

    const dateRange = computed<DateRangeFilter>(() => ({
      startDate: startDate.value,
      endDate: endDate.value,
    }))

    const trendTotals = computed(() => {
      const points = trends.value?.points || []
      return points.reduce(
        (acc, point) => {
          acc.activeUsers += point.activeUsers
          acc.tasks += point.tasks
          acc.completedTasks += point.completedTasks
          acc.failedTasks += point.failedTasks
          acc.netChargedPages += point.netChargedPages
          acc.retailValueYuan += point.retailValueYuan
          return acc
        },
        { activeUsers: 0, tasks: 0, completedTasks: 0, failedTasks: 0, netChargedPages: 0, retailValueYuan: 0 },
      )
    })

    const successRate = computed(() => {
      return roundPercent(trendTotals.value.completedTasks / (trendTotals.value.tasks || 1))
    })

    const primaryMetrics = computed(() => [
      {
        label: '新增用户',
        value: stats.value?.totalUsers || 0,
        description: '区间内完成注册的用户数',
        target: 'users' as MenuKey,
        loading: statsLoading.value,
      },
      {
        label: '活跃用户',
        value: costBenefit.value?.activeUsers || 0,
        description: '区间内至少创建过任务的用户',
        target: 'users' as MenuKey,
        loading: costBenefitLoading.value,
      },
      {
        label: '任务总量',
        value: trendTotals.value.tasks,
        description: `成功率 ${successRate.value}`,
        target: 'tasks' as MenuKey,
        loading: trendsLoading.value,
      },
      {
        label: '净扣页数',
        value: costAnalytics.value?.netChargedPages || 0,
        description: '预留页数扣除退款后的真实用量',
        target: 'tasks' as MenuKey,
        loading: analyticsLoading.value,
      },
      {
        label: '零售价值',
        value: `¥${(costAnalytics.value?.retailValueYuan || 0).toFixed(2)}`,
        description: '按当前单页零售价估算',
        target: 'points' as MenuKey,
        loading: analyticsLoading.value,
      },
      {
        label: '积分利润率',
        value: roundPercent(costBenefit.value?.profitMargin || 0),
        description: '充值积分与消耗积分的差额代理',
        target: 'points' as MenuKey,
        loading: costBenefitLoading.value,
      },
    ])

    const rangeHint = computed(() => `${startDate.value} 至 ${endDate.value}`)

    const buildSeries = (key: TrendKey) => {
      const points = trends.value?.points || []
      const max = Math.max(...points.map((point) => Number(point[key])), 1)
      return points.map((point) => ({
        date: point.date.slice(5),
        value: Number(point[key]),
        height: Math.max(4, Math.round((Number(point[key]) / max) * 100)),
      }))
    }

    const trendCards = computed(() => [
      { title: '活跃用户', total: trendTotals.value.activeUsers, points: buildSeries('activeUsers') },
      { title: '任务量', total: trendTotals.value.tasks, points: buildSeries('tasks') },
      { title: '成功任务', total: trendTotals.value.completedTasks, points: buildSeries('completedTasks') },
      { title: '净扣页数', total: trendTotals.value.netChargedPages, points: buildSeries('netChargedPages') },
    ])

    const funnelStages = computed(() => {
      if (!funnel.value) return []
      const stages = funnel.value.stages
      const registered = Math.max(stages.registered, 1)
      return [
        { label: '注册', value: stages.registered, width: 100, note: '入口基数' },
        { label: '激活', value: stages.activated, width: Math.max(4, (stages.activated / registered) * 100), note: roundPercent(funnel.value.conversionRates.registeredToActivated) },
        { label: '首次导出', value: stages.firstExport, width: Math.max(4, (stages.firstExport / registered) * 100), note: roundPercent(funnel.value.conversionRates.activatedToFirstExport) },
        { label: '回访', value: stages.returned, width: Math.max(4, (stages.returned / registered) * 100), note: roundPercent(funnel.value.conversionRates.firstExportToReturned) },
      ]
    })

    const valueList = computed(() => [
      { label: '充值积分', value: costBenefit.value?.totalRechargePoints || 0 },
      { label: '消耗积分', value: costBenefit.value?.totalCostPoints || 0 },
      { label: 'ARPU(积分)', value: Math.round((costBenefit.value?.arpuPoints || 0) * 10) / 10 },
      { label: 'WAU / MAU', value: `${frequency.value?.wau || 0} / ${frequency.value?.mau || 0}` },
      { label: '7 天回访率', value: roundPercent(frequency.value?.secondTaskRate7Day || 0) },
      { label: '积分耗尽率', value: roundPercent(willingness.value?.exhaustionRate || 0) },
    ])

    const validateRange = () => {
      if (startDate.value && endDate.value && startDate.value > endDate.value) {
        message.error('开始日期不能晚于结束日期')
        return false
      }
      return true
    }

    const loadStats = async () => {
      statsLoading.value = true
      try {
        stats.value = await getAdminStats(dateRange.value)
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load stats failed', error)
        message.error('加载统计数据失败')
      }
      finally {
        statsLoading.value = false
      }
    }

    const loadCostBenefit = async () => {
      costBenefitLoading.value = true
      try {
        costBenefit.value = await getCostBenefitAnalytics(dateRange.value)
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load cost benefit failed', error)
        message.error('加载成本收益数据失败')
      }
      finally {
        costBenefitLoading.value = false
      }
    }

    const loadAnalytics = async () => {
      analyticsLoading.value = true
      try {
        const [f, freq, cost, will] = await Promise.all([
          getFunnelAnalytics(dateRange.value).catch(() => null),
          getFrequencyAnalytics(dateRange.value).catch(() => null),
          getCostAnalytics(dateRange.value).catch(() => null),
          getWillingnessAnalytics(dateRange.value).catch(() => null),
        ])
        funnel.value = f
        frequency.value = freq
        costAnalytics.value = cost
        willingness.value = will
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load analytics failed', error)
      }
      finally {
        analyticsLoading.value = false
      }
    }

    const loadTrends = async () => {
      trendsLoading.value = true
      try {
        trends.value = await getOverviewTrends(dateRange.value)
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.error('load trends failed', error)
        message.error('加载趋势数据失败')
      }
      finally {
        trendsLoading.value = false
      }
    }

    const refreshAll = () => {
      if (!validateRange()) return
      loadStats()
      loadCostBenefit()
      loadAnalytics()
      loadTrends()
    }

    const applyPreset = (days: number) => {
      selectedPreset.value = days
      endDate.value = formatDate(new Date())
      startDate.value = daysBefore(days - 1)
      refreshAll()
    }

    const goDetail = (target: MenuKey) => {
      emit('navigate', target)
    }

    onMounted(() => {
      refreshAll()
    })

    return {
      presets,
      selectedPreset,
      startDate,
      endDate,
      rangeHint,
      primaryMetrics,
      trendCards,
      trendsLoading,
      funnelStages,
      valueList,
      analyticsLoading,
      costBenefitLoading,
      costAnalytics,
      applyPreset,
      refreshAll,
      goDetail,
    }
  },
})
